/**
 * Cliente HTTP del navegador.
 *
 * Todo lo que entra se valida contra el esquema Zod del contrato. Si el
 * servidor devuelve algo que no calza, se falla aquí —en el borde— y no tres
 * pantallas más adelante con un `undefined` (constitución, Art. 8).
 */

import { z, type ZodType } from "zod";
import {
  analysisResponseSchema,
  apiErrorSchema,
  calibrationBatchResponseSchema,
  adminUsersResponseSchema,
  calibrationProfilesResponseSchema,
  calibrationVehicleResponseSchema,
  currentUserSchema,
  fleetBatchResponseSchema,
  fleetCheckResponseSchema,
  labSampleIdentitySchema,
  magicLinkAcceptedSchema,
  vehicleListSchema,
  type AnalysisRequest,
  type AnalysisResponse,
  type CalibrationBatchRequest,
  type CalibrationBatchResponse,
  type CalibrationProfilesResponse,
  type CalibrationVehicleRequest,
  type CalibrationVehicleResponse,
  type CurrentUser,
  type FleetBatchRequest,
  type FleetBatchResponse,
  type FleetCheckRequest,
  type FleetCheckResponse,
  type AdminUsersResponse,
  type SaveCalibrationRequest,
  type SaveUserRequest,
  type VehicleList,
} from "@core/contracts/api.ts";
import {
  rastreoEventsResponseSchema,
  rastreoFleetResponseSchema,
  rastreoMapConfigSchema,
  rastreoTrackResponseSchema,
  type RastreoEventsResponse,
  type RastreoFleetResponse,
  type RastreoMapConfig,
  type RastreoTrackResponse,
} from "@core/contracts/rastreo.ts";
import {
  rastreoPendientesResponseSchema,
  rastreoRegistryResponseSchema,
  saveEquipoResponseSchema,
  type CrearEquipoForm,
  type EditarEquipoForm,
  type RastreoPendientesResponse,
  type RastreoRegistryResponse,
  type SaveEquipoResponse,
} from "@core/contracts/rastreo-registro.ts";
import {
  alertRulesResponseSchema,
  type AddAlertRuleInput,
  type AlertRulesResponse,
} from "@core/contracts/rastreo-alertas.ts";

export class ApiError extends Error {
  constructor(
    public code: string,
    message: string,
    public status: number,
  ) {
    super(message);
  }
}

/**
 * Mensaje de reserva por código, para cuando el servidor no manda uno propio.
 *
 * Antes estos textos PISABAN al mensaje del servidor, y el efecto era que
 * todas las fallas de telemetría —una página lenta, un período demasiado largo,
 * un equipo sin historial— se veían idénticas: «no se pudo consultar la
 * telemetría en este momento». El servidor sabía cuál era el problema y la
 * pantalla lo tiraba a la basura, así que ni el usuario ni yo podíamos
 * distinguirlos sin abrir los registros del Worker.
 */
const FALLBACK_MESSAGES: Record<string, string> = {
  UNAUTHENTICATED: "La sesión no está activa. Vuelve a entrar.",
  VEHICLE_SCOPE_DENIED: "No tienes acceso a eso.",
  INVALID_PERIOD: "El período solicitado no es válido.",
  INVALID_REQUEST: "La solicitud no es válida.",
  TELEMETRY_UNAVAILABLE: "No se pudo consultar la telemetría en este momento.",
  NOT_CONFIGURED: "La conexión con la plataforma WOX no está configurada.",
};

/**
 * Un resumen corto y seguro de un cuerpo que no entendimos.
 *
 * Corto porque va a una pantalla, no a un registro; y sin adornos porque el
 * objetivo es que se pueda copiar y pegar en un mensaje sin perder el dato.
 */
function describeUnexpected(payload: unknown): string {
  if (payload === null || payload === undefined) return ", ni siquiera JSON";
  if (typeof payload === "object") {
    const keys = Object.keys(payload as Record<string, unknown>).slice(0, 4);
    return keys.length ? ` (campos: ${keys.join(", ")})` : "";
  }
  return ` (${String(payload).slice(0, 80)})`;
}

/** Para las escrituras: la pantalla vuelve a leer las reglas, así que no depende del cuerpo. */
const anyJsonSchema = z.object({}).passthrough();

async function request<T>(
  path: string,
  schema: ZodType<T>,
  init?: RequestInit,
): Promise<T> {
  const response = await fetch(path, {
    ...init,
    headers: { "Content-Type": "application/json", ...(init?.headers ?? {}) },
  });

  const payload: unknown = await response.json().catch(() => null);

  if (!response.ok) {
    const parsed = apiErrorSchema.safeParse(payload);
    // El mensaje del servidor manda: es el único que sabe qué pasó de verdad.
    if (parsed.success) {
      // Con la forma correcta pero sin texto —no debería pasar— se usa el
      // mensaje de reserva de ese código, que es genérico pero no es falso.
      const message =
        parsed.data.error.trim() ||
        FALLBACK_MESSAGES[parsed.data.code] ||
        "Ocurrió un error inesperado. Si vuelve a pasar, avísale a soporte con la hora exacta.";
      throw new ApiError(parsed.data.code, message, response.status);
    }

    // Aquí la respuesta NO es un error nuestro: o el Worker se cayó antes de
    // poder redactar uno, o algo en el camino devolvió otra cosa. Decir «no se
    // pudo consultar la telemetría» sería inventar una causa —culpa a WOX, que
    // en el último caso tenía los datos perfectamente— y además tira a la
    // basura lo único que sí sabemos con certeza: el código HTTP. Se dice.
    const detail = describeUnexpected(payload);
    throw new ApiError(
      "UNEXPECTED_RESPONSE",
      `El servidor respondió ${response.status} y el cuerpo no es un error con la forma esperada${detail}. ` +
        "Esto no es una falla de la telemetría: es la aplicación la que no pudo responder.",
      response.status,
    );
  }

  const parsed = schema.safeParse(payload);
  if (!parsed.success) {
    // Contrato roto: se dice, no se sigue con datos a medias.
    throw new ApiError("CONTRACT_MISMATCH", "La respuesta del servidor no tiene la forma esperada.", 500);
  }
  return parsed.data;
}

export const fetchCurrentUser = (): Promise<CurrentUser> =>
  request("/api/auth/me", currentUserSchema);

export const requestMagicLink = (email: string) =>
  request("/api/auth/request", magicLinkAcceptedSchema, {
    method: "POST",
    body: JSON.stringify({ email }),
  });

export const logout = () =>
  request("/api/auth/logout", magicLinkAcceptedSchema.partial().passthrough(), { method: "POST" });

export const fetchVehicles = (): Promise<VehicleList> =>
  request("/api/vehicles", vehicleListSchema);

export const requestAnalysis = (body: AnalysisRequest): Promise<AnalysisResponse> =>
  request("/api/analysis", analysisResponseSchema, {
    method: "POST",
    body: JSON.stringify(body),
  });

/** Un lote de la comparación de flota. El recorrido completo lo arma `batch-walk`. */
export const requestFleetBatch = (body: FleetBatchRequest): Promise<FleetBatchResponse> =>
  request("/api/fleet/batch", fleetBatchResponseSchema, {
    method: "POST",
    body: JSON.stringify(body),
  });

/** Un lote de mediciones crudas para el laboratorio. Solo superadministrador. */
export const requestCalibrationBatch = (
  body: CalibrationBatchRequest,
): Promise<CalibrationBatchResponse> =>
  request("/api/calibration/batch", calibrationBatchResponseSchema, {
    method: "POST",
    body: JSON.stringify(body),
  });

/** Un lote de la verificación de flota de un umbral común (ADR-009). */
export const requestFleetCheck = (body: FleetCheckRequest): Promise<FleetCheckResponse> =>
  request("/api/calibration/fleet-check", fleetCheckResponseSchema, {
    method: "POST",
    body: JSON.stringify(body),
  });

/**
 * El recorrido completo de un vehículo para el banco de pruebas.
 *
 * Trae los registros crudos porque el motor vuelve a correr en el navegador
 * cada vez que se mueve un umbral. Es la petición más pesada de la aplicación,
 * y a propósito: se paga una vez y después calibrar es instantáneo.
 */
export const requestCalibrationVehicle = (
  body: CalibrationVehicleRequest,
): Promise<CalibrationVehicleResponse> =>
  request("/api/calibration/vehicle", calibrationVehicleResponseSchema, {
    method: "POST",
    body: JSON.stringify(body),
  });

export const fetchCalibrationProfiles = (): Promise<CalibrationProfilesResponse> =>
  request("/api/calibration/profiles", calibrationProfilesResponseSchema);

const savedSchema = z.object({
  ok: z.literal(true),
  version: z.number().int(),
  profileDigest: z.string(),
  /** Con alcance de vehículo: qué muestra verificó la guarda, y si es la que se veía. */
  verifiedSample: labSampleIdentitySchema.extend({ matchesLab: z.boolean().nullable() }).optional(),
  /** Advertencias p99 recalculadas por el servidor; nunca son un veto manual. */
  statisticalWarnings: z.array(z.string()).optional(),
});

/**
 * Guarda la calibración y la activa, para un vehículo o para toda la cadencia.
 * Con alcance de cadencia lleva la verificación de flota firmada (ADR-009).
 */
export const saveCalibration = (body: SaveCalibrationRequest) =>
  request("/api/calibration/save", savedSchema, {
    method: "POST",
    body: JSON.stringify(body),
  });

/**
 * Desactiva la calibración activa de UN alcance.
 *
 * Sin `scopeDevice` quita la de la cadencia y el análisis vuelve a los umbrales
 * del código. Con un equipo quita la de ese equipo, que entonces vuelve a la de
 * su cadencia. En los dos casos la fila queda en el historial: nada se borra.
 */
export const deactivateCalibration = (
  regime: "HIGH" | "MEDIUM" | "LOW",
  scopeDevice: string | null = null,
) =>
  request("/api/calibration/deactivate", z.object({ ok: z.literal(true) }), {
    method: "POST",
    body: JSON.stringify({ regime, scopeDevice }),
  });

/* --------------------------------------------------------------- *
 * Administrador de usuarios
 * --------------------------------------------------------------- */

export const fetchAdminUsers = (): Promise<AdminUsersResponse> =>
  request("/api/admin/users", adminUsersResponseSchema);

export const saveUser = (body: SaveUserRequest) =>
  request("/api/admin/users", z.object({ ok: z.literal(true), id: z.string() }), {
    method: "POST",
    body: JSON.stringify(body),
  });

export const addUserScope = (body: {
  userId: string;
  scopeType: "CARRIER" | "PLATE" | "DOCUMENT";
  value: string;
}) =>
  request("/api/admin/scope", z.object({ ok: z.literal(true) }), {
    method: "POST",
    body: JSON.stringify(body),
  });

export const removeUserScope = (scopeId: number) =>
  request("/api/admin/scope/remove", z.object({ ok: z.literal(true) }), {
    method: "POST",
    body: JSON.stringify({ scopeId }),
  });

/* --------------------------------------------------------------- *
 * Rastreo (motor propio). El navegador solo habla con el Worker.
 * --------------------------------------------------------------- */

// Las lecturas de Rastreo reciben la señal de react-query: si la persona cambia de equipo, de
// vista o esconde la pestaña, el pedido en curso se corta en vez de terminar para nadie.

export const fetchRastreoMapConfig = (signal?: AbortSignal): Promise<RastreoMapConfig> =>
  request("/api/rastreo/config", rastreoMapConfigSchema, { signal });

export const fetchRastreoFleet = (signal?: AbortSignal): Promise<RastreoFleetResponse> =>
  request("/api/rastreo/fleet", rastreoFleetResponseSchema, { signal });

const rastreoPeriodQuery = (period: { fromIso: string; toIso: string }) =>
  new URLSearchParams({ from: period.fromIso, to: period.toIso }).toString();

export const fetchRastreoTrack = (
  deviceId: string,
  period: { fromIso: string; toIso: string },
  signal?: AbortSignal,
): Promise<RastreoTrackResponse> =>
  request(
    `/api/rastreo/devices/${encodeURIComponent(deviceId)}/track?${rastreoPeriodQuery(period)}`,
    rastreoTrackResponseSchema,
    { signal },
  );

export const fetchRastreoEvents = (
  deviceId: string,
  period: { fromIso: string; toIso: string },
  signal?: AbortSignal,
): Promise<RastreoEventsResponse> =>
  request(
    `/api/rastreo/devices/${encodeURIComponent(deviceId)}/events?${rastreoPeriodQuery(period)}`,
    rastreoEventsResponseSchema,
    { signal },
  );

/* --------------------------------------------------------------- *
 * Rastreo: registro manual de equipos y clientes (specs/rastreo-registro/)
 * --------------------------------------------------------------- */

export const fetchRastreoRegistry = (signal?: AbortSignal): Promise<RastreoRegistryResponse> =>
  request("/api/rastreo/registry", rastreoRegistryResponseSchema, { signal });

/** Los GPS que ya transmiten sin registro (RR-23): sin el IMEI, solo sus últimos 4 dígitos. */
export const fetchRastreoPendientes = (signal?: AbortSignal): Promise<RastreoPendientesResponse> =>
  request("/api/rastreo/pendientes", rastreoPendientesResponseSchema, { signal });

/** El cuerpo va tal como lo escribió la persona: el Worker normaliza y valida con el mismo esquema. */
export const createRastreoEquipo = (body: CrearEquipoForm): Promise<SaveEquipoResponse> =>
  request("/api/rastreo/equipos", saveEquipoResponseSchema, { method: "POST", body: JSON.stringify(body) });

export const updateRastreoEquipo = (id: string, body: EditarEquipoForm): Promise<SaveEquipoResponse> =>
  request(`/api/rastreo/equipos/${encodeURIComponent(id)}`, saveEquipoResponseSchema, {
    method: "PUT",
    body: JSON.stringify(body),
  });


/* --------------------------------------------------------------- *
 * Rastreo: reglas de alerta por dispositivo (RR-28). Son preferencias de Drive: no le mandan
 * nada al GPS (constitución, Art. 6).
 * --------------------------------------------------------------- */

const alertRulesPath = (deviceId: string) => `/api/rastreo/devices/${encodeURIComponent(deviceId)}/alert-rules`;

export const fetchRastreoAlertRules = (deviceId: string, signal?: AbortSignal): Promise<AlertRulesResponse> =>
  request(alertRulesPath(deviceId), alertRulesResponseSchema, { signal });

/** Enciende o apaga el aviso de una alerta; el Worker responde con la regla como quedó. */
export const setRastreoAlertNotify = (deviceId: string, code: string, notify: boolean) =>
  request(`${alertRulesPath(deviceId)}/${encodeURIComponent(code)}`, anyJsonSchema, {
    method: "PUT",
    body: JSON.stringify({ notify }),
  });

export const addRastreoAlertRule = (deviceId: string, body: AddAlertRuleInput) =>
  request(alertRulesPath(deviceId), anyJsonSchema, { method: "POST", body: JSON.stringify(body) });

export const removeRastreoAlertRule = (deviceId: string, code: string) =>
  request(`${alertRulesPath(deviceId)}/${encodeURIComponent(code)}`, anyJsonSchema, { method: "DELETE" });
